import type { PropsWithChildren, ReactElement } from 'react';
import React, { forwardRef } from 'react';
import ReactModal from 'react-modal';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import View from '../View';
import Text from '../Text';
import type { ViewProps } from '../View/View';
import { space } from 'styled-system';

export interface ModalProps
  extends PropsWithChildren<Omit<ReactModal.Props, 'isOpen'>> {
  onClose: () => void;
  onPrevious?: () => void;
  previousLabel?: string;
  isVisible?: boolean;
  height?: string;
  width?: string;
  isOpen: boolean;
  minWidth?: string;
  maxWidth?: string;
  maxHeight?: string;
  viewProps?: ViewProps;
}

const Modal = ({
  children,
  onClose,
  onPrevious,
  previousLabel,
  isVisible = true,
  height = '100%',
  minWidth,
  width = '100%',
  maxWidth,
  maxHeight,
  isOpen,
  viewProps,
  ...restProps
}: ModalProps): ReactElement => {
  return (
    <ReactModal
      style={{
        content: {
          padding: 0,
          background: 'transparent',
          border: 'none',
          borderRadius: 0,
          display: isVisible ? 'flex' : 'none',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'visible',
        },
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: isVisible ? 'flex' : 'none',
          zIndex: 1000,
        },
      }}
      onRequestClose={onClose}
      isOpen={isOpen ?? true}
      shouldCloseOnOverlayClick={false}
      {...restProps}
    >
      <View
        height={height}
        width={width}
        minWidth={minWidth}
        maxWidth={maxWidth}
        maxHeight={maxHeight}
        {...viewProps}
      >
        {previousLabel && onPrevious && (
          <ModalPreviousButton label={previousLabel} onPress={onPrevious} />
        )}
        <ModalCloseButton onPress={onClose} />
        {children}
      </View>
    </ReactModal>
  );
};

const ModalPreviousButton = ({
  label,
  onPress,
}: {
  label: string;
  onPress: () => void;
}): JSX.Element => (
  <ModalPreviousButtonBg role="button" onClick={onPress}>
    <ModalButtonText>&larr;</ModalButtonText>
    <Text fontWeight="bold" color="neutral.black_900" ml={3}>
      {label ? `Back to ${label}` : 'Back'}
    </Text>
  </ModalPreviousButtonBg>
);

const ModalCloseButton = ({
  onPress,
}: {
  onPress: () => void;
}): ReactElement => (
  <ModalCloseButtonBg onClick={onPress}>
    <ModalButtonText>✕</ModalButtonText>
  </ModalCloseButtonBg>
);

const ModalPreviousButtonBg = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -20px;
  right: 50px;
  height: 40px;
  z-index: 100;
  padding: 0 ${themeGet('space.4')}px;
  border: none;
  background-color: ${themeGet('button.primary.background')};
  :hover {
    background-color: ${themeGet('button.primary.hover.background')};
  }
  cursor: pointer;
`;

const ModalCloseButtonBg = styled.button`
  border: none;
  position: absolute;
  top: -16px;
  right: -16px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${themeGet('colors.neutral.gray_50')};
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.18),
    0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  :hover {
    background-color: ${themeGet('colors.neutral.gray_160')};
  }
  z-index: 200;
  cursor: pointer;
`;

const ModalButtonText = styled(Text)`
  color: ${themeGet('button.primary.text')};
  font-size: 1.25rem;
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  background-color: ${themeGet('colors.neutral.black_500')};
  color: ${themeGet('colors.neutral.white_50')};
  border-bottom: 1px solid ${themeGet('colors.neutral.gray_600')};
  position: sticky;
  top: 0;
  z-index: 10;
  width: 100%;
  box-sizing: border-box;
  max-width: 1200px;
  margin: 0 auto;
`;

export const ModalBody = forwardRef(
  (props: PropsWithChildren<ViewProps>, ref: React.Ref<HTMLDivElement>) => {
    return (
      <View
        ref={ref}
        p={4}
        flex={1}
        bg="background"
        overflowY={props.overflowY || 'scroll'}
        {...props}
      />
    );
  },
);

export const ModalFooter = styled(View)`
  flex-direction: row;
  background-color: ${themeGet('colors.neutral.black_600')};
  padding: ${themeGet('space.3')}px;
  ${space}
  align-items: center;
  justify-content: flex-end;
`;

export const ModalTabs = styled(View)`
  flex-direction: row;
`;

const TabBackground = styled.div<{
  isActive: boolean;
  isSmall?: boolean;
}>`
  padding: ${themeGet('space.3')}px
    ${(props): string =>
      props.isSmall
        ? themeGet('space.4')(props)
        : themeGet('space.5')(props)}px;
  margin-right: ${themeGet('space.1')}px;
  background-color: ${(props): string =>
    props.isActive
      ? themeGet('colors.neutral.gray_500')(props)
      : themeGet('colors.neutral.gray_400')(props)};
  cursor: pointer;
`;

export const ModalTabItem = ({
  children,
  isActive,
  isSmall = false,
  onPress,
  label,
}: {
  children: React.ReactNode;
  isActive: boolean;
  isSmall?: boolean;
  onPress: () => void;
  label: string | undefined;
}): ReactElement => (
  <TabBackground
    role="button"
    isActive={isActive}
    isSmall={isSmall}
    onClick={onPress}
    aria-label={label}
  >
    <Text color="neutral.white_50">{children}</Text>
  </TabBackground>
);

export default Modal;
